import compareDesc from 'date-fns/compareDesc';
import _ from 'lodash-es';
import { JSX } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { GlobalsContext } from 'Shared/context';
import useFetch from 'use-http';

import { OutlineButton } from '@/components/Buttons';
import Loader from '@/components/Loader';
import { useSelector } from '@/redux/helper';
import { buildURL } from '@/shared/fetch';

import EmptyNotifications from './EmptyNotifiications';
import Notification from './Notification';
import { BellNotificationType } from './types';

interface NotificationItemProps {
  _id: string;
  title: string;
  body: string;
  createdAt: string;
  previewImage: string;
  profileImage: string;
  profile?: {
    animalIcon: string;
    color: string;
  };
  isViewed: boolean;
  type: BellNotificationType;
  actionURL: string;
}

interface NotificationSocketDataProps {
  data: NotificationItemProps[];
  total: number;
}

const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

const INITIAL_DATA: { older: NotificationItemProps[]; newer: NotificationItemProps[] } = {
  older: [],
  newer: [],
};

const Notifications = () => {
  const userId = useSelector((store) => store.user.userId);
  const [totalCount, setTotalCount] = useState(0);
  const { socket, userSocket } = useContext(GlobalsContext);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [{ older, newer }, setData] = useState(INITIAL_DATA);
  const { get: markAsRead, loading, error } = useFetch(buildURL('/notification/markasread'));

  // Subscribe notifications
  useEffect(() => {
    setIsInitialLoaded(false);
    const socketHandler = ({ data, total }: NotificationSocketDataProps) => {
      setIsInitialLoaded(true);
      setTotalCount(total);

      setData(({ older: previousOlder, newer: previousNewer }) => {
        const updatedList = _.chain(previousOlder)
          .concat(previousNewer)
          .keyBy((n) => n._id)
          .value();

        data.forEach((newNotification) => {
          const oldNotification = updatedList[newNotification._id];
          if (oldNotification) {
            updatedList[newNotification._id] = { ...oldNotification, ...newNotification };
          } else {
            updatedList[newNotification._id] = newNotification;
          }
        });

        const [older, newer] = _.chain(updatedList)
          .values()
          .sort((a, b) => compareDesc(new Date(a.createdAt), new Date(b.createdAt)))
          .partition(({ createdAt: d }) => Date.parse(d) <= weekAgo)
          .value();

        return { older, newer };
      });
    };

    if (!socket || !userSocket || !userId) {
      return undefined;
    }

    userSocket.on('receiveNotifications', socketHandler);
    socket.emit('getNotifications', { userId });
    return () => {
      userSocket.off('receiveNotifications', socketHandler);
    };
  }, [socket, userSocket, userId]);

  const notificationClickHandler = (notificationId: string) => {
    socket?.emit('viewNotification', { userId, notificationId });
  };

  const onScrollHandler: JSX.UIEventHandler<HTMLElement> = ({ currentTarget }) => {
    if (
      currentTarget.scrollHeight - currentTarget.scrollTop === currentTarget.clientHeight &&
      older.length + newer.length < totalCount &&
      socket
    ) {
      socket.emit('getNotifications', { userId, notificationId: _.last(newer)?._id ?? _.last(older)?._id });
    }
  };

  // log error
  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  const readAllButton = (
    <OutlineButton loading={loading} size="medium" label="Mark all as read" onClick={() => markAsRead()} />
  );

  return (
    <section className="customScrollBar grey min-h-full min-w-full overflow-auto bg-white" onScroll={onScrollHandler}>
      {!isInitialLoaded ? (
        <Loader fill />
      ) : newer.length === 0 && older.length === 0 ? (
        <EmptyNotifications />
      ) : (
        <div className="pb-6">
          {newer.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-6">
                <h4 className="preflight preflight-h4 font-semibold">New</h4>
                {readAllButton}
              </div>
              <ul className="preflight preflight-ul">
                {newer.map((item) => (
                  <li className="px-2" key={item._id}>
                    <Notification
                      id={item._id}
                      title={item.title}
                      description={item.body}
                      time={item.createdAt}
                      media={item.previewImage}
                      viewed={item.isViewed}
                      actionURL={item.actionURL}
                      type={item.type}
                      onClick={notificationClickHandler}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {older.length > 0 && (
            <div>
              <div className="flex items-center justify-between px-4 py-6">
                <h4 className="preflight preflight-h4 font-semibold">Older</h4>
                {newer.length === 0 && readAllButton}
              </div>
              <ul className="preflight preflight-ul">
                {older.map((item) => (
                  <li className="px-2" key={item._id}>
                    <Notification
                      id={item._id}
                      title={item.title}
                      description={item.body}
                      time={item.createdAt}
                      media={item.previewImage}
                      viewed={item.isViewed}
                      actionURL={item.actionURL}
                      type={item.type}
                      onClick={notificationClickHandler}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default Notifications;
